import { useEffect, useState } from 'react';
import { useRouter, useParams } from 'next/navigation';
import Link from 'next/link';
import { ArrowLeft, Save, Loader2, Lock, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { API_BASE } from '@/lib/api';
import { Button } from '@/components/ui/button';

interface EventData {
  id: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  sport: string;
  category: string;
  format: string;
  tier: string;
  status: string;
  season: number;
  week: number | null;
  entryFee: number;
  entryFeeType: string;
  maxEntries: number | null;
  prizePool: number;
  prizeType: string;
  prizeDistribution: Record<string, number> | null;
  entryOpenTime: string | null;
  entryDeadline: string | null;
  startTime: string;
  endTime: string;
  rarityGate: string;
  requiresCards: boolean;
  maxCardsPerEntry: number;
  cardIntegration: string;
  minCollectorScore: number;
  settings: Record<string, unknown> | null;
  entryCount: number;
  isLocked: boolean;
}

const CATEGORIES = ['WEEKLY', 'WEEKEND', 'DAILY', 'TOURNAMENT', 'SPECIAL'];
const FORMATS = ['CLASSIC', 'SHOWDOWN', 'FREE_ROLL', 'SALARY_CAP', 'BEST_BALL'];
const TIERS = ['FREE', 'STANDARD', 'PREMIUM', 'ELITE', 'CHAMPIONSHIP'];
const STATUSES = ['UPCOMING', 'OPEN', 'LOCKED', 'LIVE', 'SCORING', 'COMPLETED', 'CANCELLED'];
const ENTRY_FEE_TYPES = ['FREE', 'COINS', 'GEMS', 'USD'];
const PRIZE_TYPES = ['COINS', 'GEMS', 'USD', 'CARDS', 'MIXED'];
const RARITY_GATES = ['OPEN', 'CORE', 'RARE', 'ELITE', 'LEGENDARY', 'PRIME'];
const CARD_INTEGRATIONS = ['NONE', 'OPTIONAL', 'REQUIRED', 'CARDS_ONLY'];

export default function AdminEventEditPage() {
  const router = useRouter();
  const params = useParams();
  const eventId = params.id as string;
  const { accessToken } = useAuthStore();

  const [event, setEvent] = useState<EventData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [superadminOverride, setSuperadminOverride] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    imageUrl: '',
    category: 'WEEKLY',
    format: 'CLASSIC',
    tier: 'STANDARD',
    status: 'UPCOMING',
    season: 2025,
    week: '',
    entryFee: 0,
    entryFeeType: 'COINS',
    maxEntries: '',
    prizePool: 0,
    prizeType: 'COINS',
    entryOpenTime: '',
    entryDeadline: '',
    startTime: '',
    endTime: '',
    rarityGate: 'OPEN',
    requiresCards: true,
    maxCardsPerEntry: 8,
    cardIntegration: 'REQUIRED',
    minCollectorScore: 0,
  });

  useEffect(() => {
    fetchEvent();
  }, [eventId, accessToken]);

  const fetchEvent = async () => {
    if (!accessToken) return;

    try {
      const res = await fetch(`${API_BASE}/api/admin/events/${eventId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (res.ok) {
        const data = await res.json();
        setEvent(data.event);

        // Populate form with event data
        setFormData({
          name: data.event.name || '',
          description: data.event.description || '',
          imageUrl: data.event.imageUrl || '',
          category: data.event.category || 'WEEKLY',
          format: data.event.format || 'CLASSIC',
          tier: data.event.tier || 'STANDARD',
          status: data.event.status || 'UPCOMING',
          season: data.event.season || 2025,
          week: data.event.week?.toString() || '',
          entryFee: data.event.entryFee || 0,
          entryFeeType: data.event.entryFeeType || 'COINS',
          maxEntries: data.event.maxEntries?.toString() || '',
          prizePool: data.event.prizePool || 0,
          prizeType: data.event.prizeType || 'COINS',
          entryOpenTime: data.event.entryOpenTime ? formatDateTimeLocal(data.event.entryOpenTime) : '',
          entryDeadline: data.event.entryDeadline ? formatDateTimeLocal(data.event.entryDeadline) : '',
          startTime: data.event.startTime ? formatDateTimeLocal(data.event.startTime) : '',
          endTime: data.event.endTime ? formatDateTimeLocal(data.event.endTime) : '',
          rarityGate: data.event.rarityGate || 'OPEN',
          requiresCards: data.event.requiresCards ?? true,
          maxCardsPerEntry: data.event.maxCardsPerEntry || 8,
          cardIntegration: data.event.cardIntegration || 'REQUIRED',
          minCollectorScore: data.event.minCollectorScore || 0,
        });
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to load event');
      }
    } catch (err) {
      setError('Failed to load event');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateTimeLocal = (isoString: string) => {
    const date = new Date(isoString);
    return date.toISOString().slice(0, 16);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormData((prev) => ({ ...prev, [name]: value === '' ? '' : Number(value) }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accessToken) return;

    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload: Record<string, unknown> = {
        ...formData,
        week: formData.week ? Number(formData.week) : null,
        maxEntries: formData.maxEntries ? Number(formData.maxEntries) : null,
        entryOpenTime: formData.entryOpenTime || null,
        entryDeadline: formData.entryDeadline || null,
      };

      if (event?.isLocked && superadminOverride) {
        payload._superadminOverride = true;
      }

      const res = await fetch(`${API_BASE}/api/admin/events/${eventId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess(data.warning || 'Event updated successfully');
        setEvent(data.event);
      } else {
        setError(data.error || 'Failed to update event');
      }
    } catch (err) {
      setError('Failed to update event');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 text-red-400 animate-spin" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400">{error || 'Event not found'}</p>
        <Link href="/admin/events" className="text-zinc-400 hover:text-white mt-4 inline-block">
          Back to Events
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link
            href="/admin/events"
            className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-white">Edit Event</h1>
            <p className="text-sm text-zinc-400">{event.name}</p>
          </div>
        </div>
      </div>

      {/* Locked Warning */}
      {event.isLocked && (
        <div className="mb-6 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30 flex items-start gap-3">
          <Lock className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-yellow-400 font-medium">Event is Locked</p>
            <p className="text-yellow-400/80 text-sm mt-1">
              This event has {event.entryCount} {event.entryCount === 1 ? 'entry' : 'entries'}.
              Enable superadmin override to make changes.
            </p>
            <label className="flex items-center gap-2 mt-3 cursor-pointer">
              <input
                type="checkbox"
                checked={superadminOverride}
                onChange={(e) => setSuperadminOverride(e.target.checked)}
                className="w-4 h-4 rounded border-yellow-500/50 bg-yellow-500/20 text-yellow-500 focus:ring-yellow-500"
              />
              <span className="text-sm text-yellow-400">Enable Superadmin Override</span>
            </label>
          </div>
        </div>
      )}

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-red-400" />
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 rounded-xl bg-green-500/10 border border-green-500/30">
          <p className="text-green-400">{success}</p>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-zinc-400 mb-1">Event Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                disabled={event.isLocked && !superadminOverride}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none disabled:opacity-50"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-zinc-400 mb-1">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                disabled={event.isLocked && !superadminOverride}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none disabled:opacity-50"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-zinc-400 mb-1">Image URL</label>
              <input
                type="url"
                name="imageUrl"
                value={formData.imageUrl}
                onChange={handleChange}
                disabled={event.isLocked && !superadminOverride}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none disabled:opacity-50"
              />
            </div>
          </div>
        </div>

        {/* Classification */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Classification</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Category</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                disabled={event.isLocked && !superadminOverride}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none disabled:opacity-50"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Format</label>
              <select
                name="format"
                value={formData.format}
                onChange={handleChange}
                disabled={event.isLocked && !superadminOverride}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none disabled:opacity-50"
              >
                {FORMATS.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Tier</label>
              <select
                name="tier"
                value={formData.tier}
                onChange={handleChange}
                disabled={event.isLocked && !superadminOverride}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none disabled:opacity-50"
              >
                {TIERS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                disabled={event.isLocked && !superadminOverride}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none disabled:opacity-50"
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Season</label>
              <input
                type="number"
                name="season"
                value={formData.season}
                onChange={handleChange}
                disabled={event.isLocked && !superadminOverride}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Week (optional)</label>
              <input
                type="number"
                name="week"
                value={formData.week}
                onChange={handleChange}
                disabled={event.isLocked && !superadminOverride}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none disabled:opacity-50"
              />
            </div>
          </div>
        </div>

        {/* Entry & Prizes */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Entry & Prizes</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Entry Fee</label>
              <input
                type="number"
                name="entryFee"
                value={formData.entryFee}
                onChange={handleChange}
                min={0}
                disabled={event.isLocked && !superadminOverride}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Entry Fee Type</label>
              <select
                name="entryFeeType"
                value={formData.entryFeeType}
                onChange={handleChange}
                disabled={event.isLocked && !superadminOverride}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none disabled:opacity-50"
              >
                {ENTRY_FEE_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Max Entries (optional)</label>
              <input
                type="number"
                name="maxEntries"
                value={formData.maxEntries}
                onChange={handleChange}
                min={0}
                disabled={event.isLocked && !superadminOverride}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Prize Pool</label>
              <input
                type="number"
                name="prizePool"
                value={formData.prizePool}
                onChange={handleChange}
                min={0}
                disabled={event.isLocked && !superadminOverride}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Prize Type</label>
              <select
                name="prizeType"
                value={formData.prizeType}
                onChange={handleChange}
                disabled={event.isLocked && !superadminOverride}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none disabled:opacity-50"
              >
                {PRIZE_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Schedule */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Schedule</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Entry Open Time</label>
              <input
                type="datetime-local"
                name="entryOpenTime"
                value={formData.entryOpenTime}
                onChange={handleChange}
                disabled={event.isLocked && !superadminOverride}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Entry Deadline</label>
              <input
                type="datetime-local"
                name="entryDeadline"
                value={formData.entryDeadline}
                onChange={handleChange}
                disabled={event.isLocked && !superadminOverride}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Start Time</label>
              <input
                type="datetime-local"
                name="startTime"
                value={formData.startTime}
                onChange={handleChange}
                required
                disabled={event.isLocked && !superadminOverride}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">End Time</label>
              <input
                type="datetime-local"
                name="endTime"
                value={formData.endTime}
                onChange={handleChange}
                required
                disabled={event.isLocked && !superadminOverride}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none disabled:opacity-50"
              />
            </div>
          </div>
        </div>

        {/* Card Requirements */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Card Requirements</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Rarity Gate</label>
              <select
                name="rarityGate"
                value={formData.rarityGate}
                onChange={handleChange}
                disabled={event.isLocked && !superadminOverride}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none disabled:opacity-50"
              >
                {RARITY_GATES.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Card Integration</label>
              <select
                name="cardIntegration"
                value={formData.cardIntegration}
                onChange={handleChange}
                disabled={event.isLocked && !superadminOverride}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none disabled:opacity-50"
              >
                {CARD_INTEGRATIONS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Max Cards Per Entry</label>
              <input
                type="number"
                name="maxCardsPerEntry"
                value={formData.maxCardsPerEntry}
                onChange={handleChange}
                min={1}
                disabled={event.isLocked && !superadminOverride}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Min Collector Score</label>
              <input
                type="number"
                name="minCollectorScore"
                value={formData.minCollectorScore}
                onChange={handleChange}
                min={0}
                disabled={event.isLocked && !superadminOverride}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none disabled:opacity-50"
              />
            </div>
            <div className="flex items-center">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="requiresCards"
                  checked={formData.requiresCards}
                  onChange={handleChange}
                  disabled={event.isLocked && !superadminOverride}
                  className="w-4 h-4 rounded border-zinc-700 bg-zinc-800 text-red-500 focus:ring-red-500"
                />
                <span className="text-sm text-zinc-300">Requires Cards</span>
              </label>
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-between">
          <Link
            href="/admin/events"
            className="text-zinc-400 hover:text-white transition-colors"
          >
            Cancel
          </Link>
          <Button
            type="submit"
            disabled={isSaving || (event.isLocked && !superadminOverride)}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
